/**
 * Admin Membership Page Content editor.
 *
 * Route:  /admin/loyalty/membership-content
 *
 * The membership page is an ordered list of `sections`. Every section
 * — built-in or custom — carries an implicit order index (its position
 * in the array) and is rendered in that order on the public page.
 *
 *   • builtin sections keep their fixed identity (`key` ∈ five known
 *     values) and get their canonical heading + icon on the public
 *     page. They can be moved, but not renamed and not deleted.
 *   • custom sections carry an admin-editable heading + subheading.
 *     They can be added ("Add section" at the bottom), renamed, moved,
 *     and deleted with confirmation.
 *
 * The illustrative-offers disclaimer at the bottom of the public page
 * is fixed text and never reorderable.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, ExternalLink, Sparkles, Lock } from 'lucide-react';

// Canonical headings for built-in sections. These MUST match what the
// public page renders — the frontend is the source of truth for both.
const BUILTIN_META = {
  welcome_offers:  { label: 'Welcome offers',
                     hint:  'Visit-triggered offers on joining.' },
  privilege_card:  { label: 'Privilege card',
                     hint:  'Single ongoing benefit (percentage discount on card payments). Only one row.' },
  voucher_book:    { label: 'Voucher book',
                     hint:  'Individual vouchers issued to the member.' },
  group_and_party: { label: 'Group & party bookings',
                     hint:  'Buffet vouchers for group bookings.' },
  buy_one_offers:  { label: 'Buy-one coupons',
                     hint:  'BOGO-style coupon set.' },
};

const EMPTY_ROW = { title: '', description: '', value: '' };
const EMPTY_CONTENT = {
  title:       '',
  tagline:     '',
  price_title: '',
  price_value: '',
  price_note:  '',
  sections:    [],
  limits:      { max_custom_sections: 10 },
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);


export default function AdminMembershipContent() {
  const { API, getHeaders } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState('');
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, heading}

  useEffect(() => {
    axios.get(`${API}/admin/outlets`, { headers: getHeaders() })
      .then((res) => {
        setOutlets(res.data || []);
        if ((res.data || []).length && !outletId) setOutletId(res.data[0].id);
      })
      .catch(() => toast.error('Failed to load outlets'));
  }, [API]);

  const loadContent = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/outlets/${outletId}/membership-content`,
        { headers: getHeaders() },
      );
      setContent({ ...EMPTY_CONTENT, ...res.data });
    } catch {
      toast.error('Could not load content');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, outletId]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const setField = (key, value) => setContent((c) => ({ ...c, [key]: value }));
  const sections = content.sections || [];
  const setSections = (nextOrUpdater) =>
    setContent((c) => ({
      ...c,
      sections: typeof nextOrUpdater === 'function' ? nextOrUpdater(c.sections || []) : nextOrUpdater,
    }));

  const customCount = sections.filter((s) => s.kind === 'custom').length;
  const maxCustom = content.limits?.max_custom_sections ?? 10;
  const atCustomLimit = customCount >= maxCustom;

  const moveSection = (idx, dir) => setSections((list) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const addCustomSection = () => {
    if (atCustomLimit) {
      toast.error(`You already have the maximum ${maxCustom} custom sections. Remove some before adding more.`);
      return;
    }
    setSections((list) => [
      ...list,
      { id: uid(), kind: 'custom', key: null, heading: '', subheading: '',
        rows: [{ ...EMPTY_ROW }] },
    ]);
  };

  const requestDeleteSection = (section) => {
    setDeleteConfirm({ id: section.id, heading: section.heading || 'Untitled section' });
  };
  const confirmDeleteSection = () => {
    if (!deleteConfirm) return;
    setSections((list) => list.filter((s) => s.id !== deleteConfirm.id));
    setDeleteConfirm(null);
    toast.success('Section deleted');
  };

  const patchSection = (id, patch) =>
    setSections((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addRow = (id) =>
    patchSectionRows(id, (rows, sec) => {
      // privilege_card is capped at 1 row by product spec.
      if (sec.kind === 'builtin' && sec.key === 'privilege_card' && rows.length >= 1) return rows;
      return [...rows, { ...EMPTY_ROW }];
    });
  const removeRow = (id, idx) =>
    patchSectionRows(id, (rows) => rows.filter((_, i) => i !== idx));
  const patchRow = (id, idx, patch) =>
    patchSectionRows(id, (rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const moveRow = (id, idx, dir) =>
    patchSectionRows(id, (rows) => {
      const j = idx + dir;
      if (j < 0 || j >= rows.length) return rows;
      const next = [...rows];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  function patchSectionRows(id, mutate) {
    setSections((list) => list.map((s) => (s.id === id ? { ...s, rows: mutate(s.rows, s) } : s)));
  }

  const save = async () => {
    if (!outletId) return;
    // Client-side guard so the admin sees a clear message instead of a
    // raw backend 400.
    const missingHeading = sections.find((s) => s.kind === 'custom' && !s.heading.trim());
    if (missingHeading) {
      toast.error('Every custom section needs a heading.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title:       content.title,
        tagline:     content.tagline,
        price_title: content.price_title,
        price_value: content.price_value,
        price_note:  content.price_note,
        sections:    sections.map((s) => ({
          id:         s.id,
          kind:       s.kind,
          key:        s.kind === 'builtin' ? s.key : null,
          heading:    s.heading || '',
          subheading: s.subheading || '',
          rows:       s.rows || [],
        })),
      };
      const res = await axios.put(
        `${API}/admin/outlets/${outletId}/membership-content`,
        body, { headers: getHeaders() },
      );
      setContent({ ...EMPTY_CONTENT, ...res.data });
      toast.success('Membership content saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    if (!outletId) return;
    try {
      const res = await axios.get(
        `${API}/admin/outlets/${outletId}/hub-link`,
        { headers: getHeaders() },
      );
      const slug = res.data?.hub_slug;
      if (!slug) { toast.error('Could not resolve preview URL'); return; }
      window.open(`/hub/${slug}/membership`, '_blank');
    } catch {
      toast.error('Could not open preview');
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-membership-content">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-gray-900">
            <Sparkles className="w-6 h-6" /> Membership page content
          </h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Order sections as you like — built-in and custom sections render on the public page in
            the order shown below. The illustrative-offers disclaimer is fixed and always renders
            after every section.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="min-w-[220px]">
            <Select value={outletId} onValueChange={setOutletId}>
              <SelectTrigger data-testid="content-outlet-picker">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={openPreview}
                  disabled={!outletId}
                  data-testid="content-preview">
            <ExternalLink className="w-4 h-4 mr-1.5" /> Preview
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !outletId}
                  data-testid="content-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-gray-500">Loading…</div>
      ) : !outletId ? (
        <div className="py-10 text-center text-gray-500">Select an outlet to begin.</div>
      ) : (
        <div className="space-y-6">
          {/* Page header block */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <SectionHeader label="Page header"
                             hint="Shown at the top of the membership page — before any section." />
              <FieldRow>
                <Field label="Page title" testid="field-title">
                  <Input value={content.title}
                         onChange={(e) => setField('title', e.target.value)}
                         placeholder="Bell Tower Privilege Membership"
                         data-testid="input-title" />
                </Field>
                <Field label="Tagline" testid="field-tagline">
                  <Input value={content.tagline}
                         onChange={(e) => setField('tagline', e.target.value)}
                         placeholder="Curated perks for regular guests"
                         data-testid="input-tagline" />
                </Field>
              </FieldRow>
              <FieldRow>
                <Field label="Fee label" testid="field-price-title">
                  <Input value={content.price_title}
                         onChange={(e) => setField('price_title', e.target.value)}
                         placeholder="Annual fee"
                         data-testid="input-price-title" />
                </Field>
                <Field label="Fee value" testid="field-price-value">
                  <Input value={content.price_value}
                         onChange={(e) => setField('price_value', e.target.value)}
                         placeholder="₹ 5,000"
                         data-testid="input-price-value" />
                </Field>
                <Field label="Fee note" testid="field-price-note">
                  <Input value={content.price_note}
                         onChange={(e) => setField('price_note', e.target.value)}
                         placeholder="Auto-renews annually"
                         data-testid="input-price-note" />
                </Field>
              </FieldRow>
            </CardContent>
          </Card>

          {/* Sections list (ordered) */}
          {sections.map((section, idx) => (
            <SectionCard
              key={section.id}
              section={section}
              index={idx}
              lastIndex={sections.length - 1}
              onMove={(dir) => moveSection(idx, dir)}
              onRename={(patch) => patchSection(section.id, patch)}
              onDelete={() => requestDeleteSection(section)}
              onAddRow={() => addRow(section.id)}
              onRemoveRow={(rowIdx) => removeRow(section.id, rowIdx)}
              onPatchRow={(rowIdx, patch) => patchRow(section.id, rowIdx, patch)}
              onMoveRow={(rowIdx, dir) => moveRow(section.id, rowIdx, dir)}
            />
          ))}

          {/* Add custom section */}
          <div>
            <Button
              variant="outline"
              onClick={addCustomSection}
              disabled={atCustomLimit}
              data-testid="add-custom-section"
              className="w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add section
            </Button>
            <p className="text-[11px] text-gray-500 mt-2" data-testid="custom-section-limit">
              {atCustomLimit
                ? `You have reached the ${maxCustom}-section limit. Delete a custom section to add another.`
                : `Custom sections used: ${customCount} of ${maxCustom}.`}
            </p>
          </div>

          {/* Disclaimer callout — read-only, always renders on public page */}
          <Card>
            <CardContent className="p-5 space-y-2">
              <SectionHeader label="Disclaimer" hint="Fixed text — always renders after every section on the public page." />
              <div className="text-xs text-gray-700 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 leading-relaxed"
                   data-testid="content-disclaimer-preview">
                All offers, values and privileges shown on this page are illustrative and subject
                to change. Actual entitlements will be confirmed at the time of enrolment. This
                form only registers your interest; it does not enrol you in the programme.
              </div>
              <p className="text-[11px] text-gray-500">
                Not editable and not reorderable.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent data-testid="delete-section-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this section?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;<strong>{deleteConfirm?.heading}</strong>&rdquo; and all its rows will be
              removed. This cannot be undone until you save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-section-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteSection}
                               className="bg-rose-600 hover:bg-rose-700"
                               data-testid="delete-section-confirm-yes">
              Delete section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   Section card — used for both built-in and custom sections.
   The two differ only in whether heading is editable and whether
   delete is available; the row editor is identical.
   ═══════════════════════════════════════════════════════════════ */
function SectionCard({
  section, index, lastIndex, onMove, onRename, onDelete,
  onAddRow, onRemoveRow, onPatchRow, onMoveRow,
}) {
  const isBuiltin = section.kind === 'builtin';
  const builtinMeta = isBuiltin ? BUILTIN_META[section.key] : null;
  const label = isBuiltin ? (builtinMeta?.label || section.key) : (section.heading || 'Untitled section');
  const isPrivilege = isBuiltin && section.key === 'privilege_card';
  const canAddRow = !isPrivilege || (section.rows.length < 1);

  return (
    <Card data-testid={`section-card-${section.id}`}
          data-section-kind={section.kind}
          data-section-key={section.key || ''}>
      <CardContent className="p-5 space-y-3">
        {/* Header row: move controls · label · type badge · delete (custom only) */}
        <div className="flex items-start gap-3">
          <div className="flex flex-col gap-0.5 pt-0.5">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                    onClick={() => onMove(-1)} disabled={index === 0}
                    data-testid={`section-move-up-${section.id}`}
                    title="Move up">
              <ArrowUp className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                    onClick={() => onMove(+1)} disabled={index === lastIndex}
                    data-testid={`section-move-down-${section.id}`}
                    title="Move down">
              <ArrowDown className="w-3.5 h-3.5" />
            </Button>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isBuiltin ? (
                <>
                  <span className="text-base font-semibold text-gray-900"
                        data-testid={`section-label-${section.id}`}>
                    {label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide
                                   text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">
                    <Lock className="w-3 h-3" /> Built-in
                  </span>
                </>
              ) : (
                <>
                  <Input
                    value={section.heading}
                    onChange={(e) => onRename({ heading: e.target.value })}
                    placeholder="Section heading"
                    className="max-w-md font-semibold"
                    data-testid={`section-heading-${section.id}`}
                  />
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide
                                   text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-full px-2 py-0.5">
                    Custom
                  </span>
                </>
              )}
            </div>
            {isBuiltin ? (
              <p className="text-xs text-gray-500 mt-0.5">{builtinMeta?.hint || ''}</p>
            ) : (
              <Input
                value={section.subheading || ''}
                onChange={(e) => onRename({ subheading: e.target.value })}
                placeholder="Section subheading (optional)"
                className="mt-2 max-w-md text-sm"
                data-testid={`section-subheading-${section.id}`}
              />
            )}
          </div>

          {!isBuiltin ? (
            <Button variant="ghost" size="sm"
                    onClick={onDelete}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`section-delete-${section.id}`}
                    title="Delete section">
              <Trash2 className="w-4 h-4" />
            </Button>
          ) : null}
        </div>

        {/* Row editor */}
        {section.rows.length === 0 ? (
          <div className="text-xs text-gray-500 border border-dashed border-gray-200 rounded-md
                          py-4 text-center"
               data-testid={`section-empty-${section.id}`}>
            No rows yet. This section will be hidden on the public page until at least one row is
            added.
          </div>
        ) : (
          <div className="space-y-2">
            {section.rows.map((r, ri) => (
              <div key={ri}
                   className="grid grid-cols-12 gap-2 items-start border border-gray-100 rounded-md p-3"
                   data-testid={`row-${section.id}-${ri}`}>
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Title</Label>
                  <Input value={r.title || ''}
                         onChange={(e) => onPatchRow(ri, { title: e.target.value })}
                         data-testid={`input-title-${section.id}-${ri}`} />
                </div>
                <div className="col-span-12 md:col-span-5">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Description</Label>
                  <Input value={r.description || ''}
                         onChange={(e) => onPatchRow(ri, { description: e.target.value })}
                         data-testid={`input-description-${section.id}-${ri}`} />
                </div>
                <div className="col-span-8 md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Value</Label>
                  <Input value={r.value || ''}
                         onChange={(e) => onPatchRow(ri, { value: e.target.value })}
                         data-testid={`input-value-${section.id}-${ri}`} />
                </div>
                <div className="col-span-4 md:col-span-1 flex flex-col gap-1 pt-4">
                  <Button variant="ghost" size="sm" onClick={() => onMoveRow(ri, -1)}
                          disabled={ri === 0}
                          data-testid={`row-up-${section.id}-${ri}`}
                          title="Move up" className="h-7 w-7 p-0">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onMoveRow(ri, +1)}
                          disabled={ri === section.rows.length - 1}
                          data-testid={`row-down-${section.id}-${ri}`}
                          title="Move down" className="h-7 w-7 p-0">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onRemoveRow(ri)}
                          data-testid={`row-remove-${section.id}-${ri}`}
                          title="Remove row"
                          className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add row */}
        <div>
          <Button variant="outline" size="sm" onClick={onAddRow}
                  disabled={!canAddRow}
                  data-testid={`add-row-${section.id}`}>
            <Plus className="w-4 h-4 mr-1" /> Add row
          </Button>
          {isPrivilege ? (
            <span className="text-[11px] text-gray-500 ml-2">
              Privilege card holds a single row.
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}


function SectionHeader({ label, hint }) {
  return (
    <div>
      <div className="text-sm font-semibold text-gray-900">{label}</div>
      {hint ? <div className="text-xs text-gray-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-wide text-gray-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
